/**
 * useTransactionData Hook
 * 
 * Clean hook for transaction list data management.
 * Handles fetching, filtering, pagination - components just consume.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks/redux';
import type { TransactionQueryParams } from '@/app/types/transactions';
import {
  fetchTransactions,
  setFilters,
  setPage,
  setPageSize,
  clearFilters,
  selectTransactions,
  selectTransactionsTotalCount,
  selectTransactionsCurrentPage,
  selectTransactionsPageSize,
  selectTransactionsFilters,
  selectTransactionsIsLoading,
  selectTransactionsError,
  selectTransactionsTotalPages,
  PAGE_SIZE_OPTIONS,
  type PageSizeOption,
} from '@/app/store/transactionsSlice';

export function useTransactionData() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const lastPathnameRef = useRef<string>('');
  
  // Select all state
  const transactions = useAppSelector(selectTransactions);
  const totalCount = useAppSelector(selectTransactionsTotalCount);
  const currentPage = useAppSelector(selectTransactionsCurrentPage);
  const pageSize = useAppSelector(selectTransactionsPageSize);
  const filters = useAppSelector(selectTransactionsFilters);
  const isLoading = useAppSelector(selectTransactionsIsLoading);
  const error = useAppSelector(selectTransactionsError);
  const totalPages = useAppSelector(selectTransactionsTotalPages);

  // Initial fetch on mount
  useEffect(() => {
    if (transactions.length === 0 && !isLoading && !error) {
      dispatch(fetchTransactions());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when returning to list page route
  useEffect(() => {
    const isReturningToList = location.pathname === '/transactions' && 
                              lastPathnameRef.current !== '/transactions' &&
                              lastPathnameRef.current !== '';
    
    if (isReturningToList && !isLoading && !error) {
      dispatch(fetchTransactions());
    }
    
    lastPathnameRef.current = location.pathname;
  }, [location.pathname, isLoading, error, dispatch]);

  // Focus/visibility handling - refetch when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isLoading && !error) {
        dispatch(fetchTransactions());
      }
    };

    const handleFocus = () => {
      if (!isLoading && !error) {
        dispatch(fetchTransactions());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isLoading, error, dispatch]);

  // Apply filters and fetch
  const applyFilters = useCallback((newFilters: Partial<TransactionQueryParams>) => {
    dispatch(setFilters(newFilters));
    dispatch(fetchTransactions({ ...filters, ...newFilters, offset: 0 }));
  }, [dispatch, filters]);

  // Change page
  const goToPage = useCallback((page: number) => {
    dispatch(setPage(page));
    dispatch(fetchTransactions({ ...filters, offset: (page - 1) * pageSize }));
  }, [dispatch, filters, pageSize]);

  // Refresh current view
  const refresh = useCallback(() => {
    dispatch(fetchTransactions());
  }, [dispatch]);

  // Clear all filters
  const resetFilters = useCallback(() => {
    dispatch(clearFilters());
    dispatch(fetchTransactions());
  }, [dispatch]);

  // Change page size
  const changePageSize = useCallback((size: number) => {
    // Validate size is one of the allowed options
    if (PAGE_SIZE_OPTIONS.includes(size as PageSizeOption)) {
      dispatch(setPageSize(size as PageSizeOption));
      dispatch(fetchTransactions({ ...filters, limit: size, offset: 0 }));
    }
  }, [dispatch, filters]);

  return {
    // Data
    transactions,
    totalCount,
    
    // Pagination
    currentPage,
    pageSize,
    totalPages,
    
    // Filters
    filters,
    
    // Status
    isLoading,
    error,
    
    // Actions
    applyFilters,
    goToPage,
    changePageSize,
    refresh,
    resetFilters,
    
    // Constants
    pageSizeOptions: PAGE_SIZE_OPTIONS,
  };
}

